import { MonitorAlertEventDto } from '@/app/Alert/types';
import { FilterFieldConfig } from '@/hooks/useJsonFilters';
import { Comparator, EnumAlertStatus, MonitoringEventSearchDto, } from '@/lib/api/types.gen';
import { formatInTimeZone } from 'date-fns-tz';

// This function is used to parse a date string without timezone conversion
// As, by default, new Date() will convert the date to the current timezone
export const parseDateWithoutTimezone = (dateStr: string): Date => {
  const [datePart, timePart] = dateStr.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  
  if (!timePart) {
    return new Date(year, month - 1, day);
  }
  
  const [time, milliseconds] = timePart.split('.');
  const [hours, minutes, seconds] = time.split(':').map(Number);
  const ms = milliseconds ? Number(milliseconds) : 0;
  
  return new Date(year, month - 1, day, hours, minutes, seconds, ms);
};

export function sortEventsByDateAscending(events: MonitorAlertEventDto[] | MonitoringEventSearchDto[]) {
  return [...events].sort((a, b) => {
    if (!a.occurred && !b.occurred) return 0;
    if (!a.occurred) return 1;
    if (!b.occurred) return -1;
    return new Date(a.occurred).getTime() - new Date(b.occurred).getTime();
  });
}

/** @deprecated Use `<Time>` component from `@/components/ui/time` instead */
export const formatDate = (date?: string | Date, timeZone?: string) => {
  if (!date) return 'N/A';
  return formatInTimeZone(new Date(date), timeZone ?? '', 'PPp');
};

/** @deprecated Use `<Time>` component from `@/components/ui/time` instead */
export const formatTime = (date?: string | Date, timeZone?: string) => {
  if (!date || !timeZone) return 'N/A';
  return formatInTimeZone(new Date(date), timeZone ?? '', 'h:mm:ssaaaa').toUpperCase();
};

const NON_HUMAN_AUTHORS = new Set(['ai', 'system', 'quill', '']);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getInvolvedHumans(alert: any): string[] {
  const names = new Set<string>();
  if (alert.actionedBy) names.add(alert.actionedBy);
  if (alert.recentNotes) {
    for (const note of alert.recentNotes) {
      const author = (note.author || '').trim();
      if (author && !NON_HUMAN_AUTHORS.has(author.toLowerCase())) {
        names.add(author);
      }
    }
  }
  return Array.from(names);
}

export function getDeviceNameByNvrId(nvrId: string | undefined, alert: any): string {
  if (!nvrId || !alert?.project?.assets) return 'Unknown Device';

  // Find the asset that has a camera with matching videoProcessorId
  const asset = alert.project.assets.find((asset: any) =>
    asset.cameras?.some((camera: any) => camera.videoProcessorId === nvrId)
  );

  return asset?.name || 'Unknown Device';
}

export const baseAlertTableFilterConfig: FilterFieldConfig[] = [
  {
    type: 'text',
    name: 'projectName',
    label: 'Site Name',
    field: 'projectName',
    comparator: Comparator.ILike,
    placeholder: 'Site name...',
  },
  {
    type: 'text',
    name: 'companyName',
    label: 'Customer Name',
    field: 'companyName',
    comparator: Comparator.ILike,
    placeholder: 'Customer name...',
  },
  {
    type: 'date',
    name: 'occurredFrom',
    label: 'Occurred From',
    field: 'occurred',
    comparator: Comparator.GtEq,
    placeholder: 'From date',
  },
  {
    type: 'date',
    name: 'occurredTo',
    label: 'Occurred To',
    field: 'occurred',
    comparator: Comparator.LtEq,
    placeholder: 'To date',
  },
  {
    type: 'toggle',
    name: 'status',
    label: 'Status',
    field: 'status',
    comparator: Comparator.Eq,
    placeholder: 'Any status',
    options: Object.values(EnumAlertStatus).map((s) => ({ label: s, value: s })),
  },
  {
    type: 'toggle',
    name: 'actioned',
    label: 'Actioned',
    field: 'actioned',
    comparator: Comparator.Eq,
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    type: 'toggle',
    name: 'isClosed',
    label: 'Closed',
    field: 'isClosed',
    comparator: Comparator.Eq,
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    type: 'date',
    name: 'closedAtFrom',
    label: 'Closed From',
    field: 'closedAt',
    comparator: Comparator.GtEq,
    placeholder: 'From date',
  },
  {
    type: 'date',
    name: 'closedAtTo',
    label: 'Closed To',
    field: 'closedAt',
    comparator: Comparator.LtEq,
    placeholder: 'To date',
  },
  {
    type: 'toggle',
    name: 'hadSlaBreach',
    label: 'SLA Breached',
    field: 'hadSlaBreach',
    comparator: Comparator.Eq,
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
];

export function getAlertTableFilterConfig(
  options: { showProjectName?: boolean } = {}
) {
  const { showProjectName = true } = options;
  if (showProjectName) return baseAlertTableFilterConfig;
  return baseAlertTableFilterConfig.filter((f) => f.name !== 'projectName');
}